import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Phone, Trash2, AlertTriangle, ArrowLeft, Shield } from 'lucide-react';

import { API } from '../lib/api';

export default function AccountSettingsPage() {
  const { user, token, logout, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(1);
  const [deleting, setDeleting] = useState(false);

  // Redirect if not authenticated
  if (!authLoading && !user) {
    navigate('/login');
    return null;
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmStep === 1) {
      setDeleteConfirmStep(2);
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'en' ? 'Account deleted successfully' : 'Account eliminato con successo');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(language === 'en' ? 'Error deleting account' : 'Errore durante l\'eliminazione dell\'account');
    } finally {
      setDeleting(false);
    }
  };

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDeleteConfirmStep(1);
  };

  if (authLoading || !user) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="account-settings-page">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[#4A5568] hover:text-[#C5A059] mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-manrope">{language === 'en' ? 'Back to Dashboard' : 'Torna alla Dashboard'}</span>
        </button>

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="font-cinzel text-3xl text-[#1A202C] mb-2">
            {language === 'en' ? 'Account Settings' : 'Impostazioni Account'}
          </h1>
          <p className="font-manrope text-[#4A5568]">
            {language === 'en' ? 'Manage your account preferences' : 'Gestisci le preferenze del tuo account'}
          </p>
        </div>

        {/* Profile Info Card */}
        <Card className="mb-6 border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="font-cinzel text-xl flex items-center gap-2">
              <User className="w-5 h-5 text-[#C5A059]" />
              {language === 'en' ? 'Profile Information' : 'Informazioni Profilo'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-[#F9F9F7] rounded-lg">
              <User className="w-5 h-5 text-[#4A5568]" />
              <div>
                <p className="text-sm text-[#4A5568]">{language === 'en' ? 'Name' : 'Nome'}</p>
                <p className="font-manrope font-medium text-[#1A202C]">{user.nome} {user.cognome}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-[#F9F9F7] rounded-lg">
              <Mail className="w-5 h-5 text-[#4A5568]" />
              <div>
                <p className="text-sm text-[#4A5568]">Email</p>
                <p className="font-manrope font-medium text-[#1A202C]">{user.email}</p>
              </div>
            </div>
            {user.telefono && (
              <div className="flex items-center gap-3 p-3 bg-[#F9F9F7] rounded-lg">
                <Phone className="w-5 h-5 text-[#4A5568]" />
                <div>
                  <p className="text-sm text-[#4A5568]">{language === 'en' ? 'Phone' : 'Telefono'}</p>
                  <p className="font-manrope font-medium text-[#1A202C]">{user.telefono}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Privacy & Data Card */}
        <Card className="mb-6 border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="font-cinzel text-xl flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#C5A059]" />
              {language === 'en' ? 'Privacy & Data' : 'Privacy e Dati'}
            </CardTitle>
            <CardDescription className="font-manrope">
              {language === 'en' 
                ? 'Control your personal data and privacy settings'
                : 'Controlla i tuoi dati personali e le impostazioni di privacy'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#4A5568] font-manrope mb-4">
              {language === 'en'
                ? 'Your data is stored securely and used only to provide our services. You can request deletion of your account and all associated data at any time.'
                : 'I tuoi dati sono conservati in modo sicuro e utilizzati solo per fornire i nostri servizi. Puoi richiedere l\'eliminazione del tuo account e di tutti i dati associati in qualsiasi momento.'}
            </p>
          </CardContent>
        </Card>

        {/* Delete Account Card - PROMINENT */}
        <Card className="border-2 border-red-200 bg-red-50" data-testid="delete-account-section">
          <CardHeader>
            <CardTitle className="font-cinzel text-xl text-red-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {language === 'en' ? 'Delete Account' : 'Elimina Account'}
            </CardTitle>
            <CardDescription className="font-manrope text-red-600">
              {language === 'en'
                ? 'Permanently delete your account and all associated data'
                : 'Elimina permanentemente il tuo account e tutti i dati associati'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-red-200">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700 font-manrope">
                  <p className="font-semibold mb-2">
                    {language === 'en' ? 'This action cannot be undone:' : 'Questa azione non può essere annullata:'}
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>{language === 'en' ? 'All your personal data will be deleted' : 'Tutti i tuoi dati personali saranno eliminati'}</li>
                    <li>{language === 'en' ? 'All your check-ins will be removed' : 'Tutti i tuoi check-in saranno rimossi'}</li>
                    <li>{language === 'en' ? 'All your bookings will be deleted' : 'Tutte le tue prenotazioni saranno eliminate'}</li>
                    <li>{language === 'en' ? 'Your loyalty points will be lost' : 'I tuoi punti fedeltà andranno persi'}</li>
                  </ul>
                </div>
              </div>

              <Button
                variant="destructive"
                size="lg"
                className="w-full bg-red-600 hover:bg-red-700 text-white font-manrope"
                onClick={() => setShowDeleteDialog(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-5 h-5 mr-2" />
                {language === 'en' ? 'Delete My Account' : 'Elimina il mio account'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={closeDeleteDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-cinzel text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                {deleteConfirmStep === 1 
                  ? (language === 'en' ? 'Delete Account?' : 'Eliminare l\'account?')
                  : (language === 'en' ? 'Final Confirmation' : 'Conferma Finale')}
              </DialogTitle>
              <DialogDescription className="font-manrope text-[#4A5568]">
                {deleteConfirmStep === 1 
                  ? (language === 'en' 
                      ? 'Are you sure you want to delete your account? This action cannot be undone.'
                      : 'Sei sicuro di voler eliminare il tuo account? Questa azione non può essere annullata.')
                  : (language === 'en'
                      ? 'This is your last chance. All your data will be permanently deleted.'
                      : 'Questa è la tua ultima possibilità. Tutti i tuoi dati saranno eliminati permanentemente.')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-700 font-manrope">
                  <strong>{user?.email}</strong>
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {language === 'en' 
                    ? 'This account and all associated data will be deleted.'
                    : 'Questo account e tutti i dati associati saranno eliminati.'}
                </p>
              </div>
            </div>

            <DialogFooter className="flex gap-3">
              <Button
                variant="outline"
                onClick={closeDeleteDialog}
                className="flex-1"
                disabled={deleting}
              >
                {language === 'en' ? 'Cancel' : 'Annulla'}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting 
                  ? (language === 'en' ? 'Deleting...' : 'Eliminazione...')
                  : deleteConfirmStep === 1 
                    ? (language === 'en' ? 'Yes, Delete' : 'Sì, Elimina')
                    : (language === 'en' ? 'Delete Forever' : 'Elimina per Sempre')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
